import { styled, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { RouteFn } from '@/hooks/use-flow';

export const SuccessAnimation = ({ route }: { route: RouteFn }) => {
  const ANIMATION_LENGTH = 2500;

  const animationContainer = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const inverseColor = theme.palette.background.inverse;
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  useEffect(() => {
    if (animationContainer.current) {
      const confettiRef = confetti.create(animationContainer.current, { resize: true });
      setTimeout(
        () =>
          confettiRef({
            particleCount: 70,
            startVelocity: 35,
            ticks: 180,
            spread: 140,
            angle: 90,
            shapes: ['square'],
            gravity: 0.5,
            scalar: 1.25,
            origin: {
              x: 0.5,
              y: 0.55,
            },
            colors: [primaryColor, primaryColor, inverseColor],
          }),
        1000
      );
    }
  }, [primaryColor, inverseColor, route]);

  useEffect(() => {
    const timer = setTimeout(() => setIsAnimationComplete(true), ANIMATION_LENGTH);
    return () => clearTimeout(timer);
  }, []);

  return (
    <StyledContainer isAnimationComplete={isAnimationComplete}>
      <StyledCanvas ref={animationContainer} />
      <StyledCheckmark>
        <svg
          id='successAnimation'
          className='animated'
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 70 70'
        >
          <circle
            className='successAnimationResult successAnimationScale'
            cx='35'
            cy='35'
            r='24'
            stroke={primaryColor}
            strokeWidth='3'
            strokeLinecap='square'
            fill={primaryColor}
          />
          <polyline
            className='successAnimationResult successAnimationScale'
            stroke={theme.palette.background.default}
            strokeWidth='3'
            points='22 36 30 44 48 27'
            fill='transparent'
          />
          <circle
            id='successAnimationCircle'
            cx='35'
            cy='35'
            r='24'
            stroke={primaryColor}
            strokeWidth='3'
            strokeLinecap='square'
            fill='transparent'
          />
          <polyline
            id='successAnimationCheck'
            stroke={primaryColor}
            strokeWidth='3'
            points='22 36 30 44 48 27'
            fill='transparent'
          />
        </svg>
      </StyledCheckmark>
    </StyledContainer>
  );
};

interface StyledContainerProps {
  isAnimationComplete: boolean;
}

const StyledContainer = styled('div')<StyledContainerProps>`
  position: fixed;
  inset: 0;
  z-index: 999;
  background-color: var(--mui-palette-background-main);
  transition: opacity 750ms ease-out;
  opacity: ${({ isAnimationComplete }) => (isAnimationComplete ? 0 : 1)};
  pointer-events: ${({ isAnimationComplete }) => (isAnimationComplete ? 'none' : 'auto')};
`;

const StyledCanvas = styled('canvas')`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const StyledCheckmark = styled('div')`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  #successAnimation {
    display: block;
    height: 140px;
    width: 140px;
  }

  #successAnimationCircle {
    stroke-dasharray: 151px 151px;
    stroke: ${({ theme }) => theme.palette.primary.main};
  }

  #successAnimationCheck {
    stroke-dasharray: 36px 36px;
    stroke: ${({ theme }) => theme.palette.primary.main};
  }

  #successAnimationResult {
    opacity: 0;
  }

  .animated {
    animation: 1s ease-out 0s 1 both scaleAnimation;
  }
  .animated #successAnimationCircle {
    animation:
      1s cubic-bezier(0.77, 0, 0.175, 1) 0s 1 both drawCircle,
      0.3s linear 0.9s 1 both fadeOut;
  }
  .animated #successAnimationCheck {
    animation:
      1s cubic-bezier(0.77, 0, 0.175, 1) 0s 1 both drawCheck,
      0.3s linear 0.9s 1 both fadeOut;
  }
  .animated .successAnimationResult {
    transform-origin: center;
    animation:
      0.3s linear 0.9s both fadeIn,
      bounce 0.5s ease-in-out 1s both;
  }

  @keyframes scaleAnimation {
    0% {
      opacity: 0;
      transform: scale(1.5);
    }
    75% {
      opacity: 1;
      transform: scale(1);
    }
    75%,
    100% {
      transform: none;
    }
  }

  @keyframes bounce {
    0%,
    100% {
      transform: none;
    }

    50% {
      transform: scale(1.05);
    }
  }

  @keyframes drawCircle {
    0% {
      stroke-dashoffset: 151px;
    }
    100% {
      stroke-dashoffset: 0;
    }
  }

  @keyframes drawCheck {
    0% {
      stroke-dashoffset: 36px;
    }
    100% {
      stroke-dashoffset: 0;
    }
  }

  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;
